import React, { useCallback, useState, useRef, useMemo, useEffect } from 'react';
import { AgGridReact } from '@ag-grid-community/react';
import {
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
  Grid,
  Box,
  Link,
  CircularProgress,
  Backdrop,
} from '@material-ui/core';
import { GridReadyEvent } from '@ag-grid-community/core';
import { connect } from 'react-redux';
import { AppState, AppThunkDispatch } from 'src/store';
import { useGoBack, useGoForward, WizardActions, WizardSection } from './BulkImport.utils';
import { commitBulkImport, uploadMutationTemplate } from './BulkImport.actions';
import { BulkImportLoadingState, BulkImportNotice } from './BulkImport.slice';
import { isNil } from 'lodash';
import Subheader from 'src/components/Subheader/Subheader.container';
import { BULKIMPORT_API } from './BulkImport.client';
import { bulkImportStyles } from './BulkImport.styles';
import { toast } from 'react-toastify';
import { makeUpdateAssortmentFabSensitive } from 'src/pages/AssortmentBuild/HigherOrder/UpdateAssortmentFab';
import { BulkImportComponent } from 'configuration-schemas/confdefnComponents';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { generateReportUrl } from 'src/components/Reporting/ReportCard';
import { maybeGetComponentProps } from 'src/utils/Functions/epicsFunctions';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { currentBearerToken } from 'src/services/axios';

type BulkImportOwnProps = Record<string, unknown>;
type BulkImportValueProps = ReturnType<typeof mapStateToProps>;
interface BulkImportDispatchProps extends ReturnType<typeof mapDispatchToProps> {}

type BulkImportBaseProps = Omit<BulkImportValueProps, never> & {
  importId?: string;
  scopeId?: string;
};
export type BulkImportProps = BulkImportBaseProps & BulkImportDispatchProps & BulkImportOwnProps;

const mapStateToProps = (state: AppState) => {
  const { bulkImportLoadingState } = state.bulkImport;
  const cprops = maybeGetComponentProps<BulkImportComponent>(state, ConfDefnComponentType.bulkImport);
  if (!cprops) {
    return {
      bulkImportLoadingState,
      importId: undefined,
      appName: state.perspective.selected?.appType,
      title: '',
      stepper1Label: '',
      stepper2Label: '',
      templateType: '',
    };
  }
  // using scopeId to deftect scope changes
  const scopeId = state.scope.scope.scopeId;
  return {
    bulkImportLoadingState,
    importId: cprops.importId,
    appName: state.perspective.selected?.appType,
    stepper1Label: cprops.stepper1Label,
    stepper2Label: cprops.stepper2Label,
    title: cprops?.title,
    templateType: cprops.templateType,
    scopeId,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    dispatchedUploadMutationTemplate: (payload: { importId: string; formData: FormData }) =>
      dispatch(uploadMutationTemplate(payload)),
    dispatchedCommit: (importId: string, txId: string) => dispatch(commitBulkImport({ importId, runId: txId })),
  };
};

const BulkImport = (props: BulkImportProps) => {
  const {
    dispatchedUploadMutationTemplate,
    bulkImportLoadingState,
    dispatchedCommit,
    importId,
    stepper1Label,
    stepper2Label,
    appName,
    title,
    templateType,
    scopeId,
  } = props;
  const uploadButtonRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<BulkImportNotice | null>(null);
  const [expanded, setExpanded] = useState(0);
  const [uploadFileName, setUploadFileName] = useState<string | null>(null);
  const [isCommitting, setIsCommitting] = useState(false);

  const downloadTemplateUrl = useMemo(() => {
    if (isNil(importId) || isNil(appName)) return;
    const bearerToken = currentBearerToken.value;
    switch (templateType) {
      case 'import':
        return `asst/${BULKIMPORT_API}/${importId}/template?token=${encodeURIComponent(
          bearerToken,
        )}&appName=${appName}`;
      case 'report':
        const dummyReport = {
          modelId: importId,
          templateId: importId,
          perspective: appName,
        };
        return window.location.origin + generateReportUrl(dummyReport);
      default:
        return `asst/${BULKIMPORT_API}/${importId}/template?token=${encodeURIComponent(
          bearerToken,
        )}&appName=${appName}`;
    }
  }, [appName, importId, templateType]);

  const downloadResultUrl = useMemo(() => {
    if (isNil(importId) || isNil(status)) return;
    const bearerToken = currentBearerToken.value;
    return `asst/${BULKIMPORT_API}/${importId}/result/${status.transactionId}?token=${encodeURIComponent(
      bearerToken,
    )}&appName=${appName}`;
  }, [appName, importId, status]);

  /* Stepper methods */
  const goForward = useGoForward(setExpanded, 2);
  const goBack = useGoBack(setExpanded);

  /* Dropdown methods*/

  const handleChangeFileUpload = useCallback(
    async (evt: React.ChangeEvent<HTMLInputElement>) => {
      setStatus(null); // reset notices on upload
      const fileList = evt.target.files;
      if (!fileList || !importId) return;
      const excelFile = fileList[0];
      setUploadFileName(excelFile.name);
      const formData = new FormData();

      formData.append('bulkimport-upload', excelFile);
      const mutationUpload = await dispatchedUploadMutationTemplate({ importId, formData });
      if (uploadMutationTemplate.fulfilled.match(mutationUpload)) {
        setStatus(mutationUpload.payload);
        setExpanded(2);
      }
      if (uploadMutationTemplate.rejected.match(mutationUpload)) {
        toast.error('An error occured fetching your upload feedback', {
          position: toast.POSITION.TOP_LEFT,
        });
      }
    },
    [dispatchedUploadMutationTemplate, importId],
  );

  const handleOnClickClearFiles = useCallback(() => {
    // manually clear the file list when the button is clicked
    // so that if the user selects opens the input twice and selects the same file,
    // it will still trigger the onChange
    if (uploadButtonRef.current) {
      uploadButtonRef.current.value = '';
    }
  }, []);

  const handleClickDownloadTemplate = useCallback(() => {
    if (!downloadTemplateUrl) {
      return;
    }
    if (templateType === 'report') {
      // reports need window.open so filename stays correct
      window.open(downloadTemplateUrl);
    } else {
      // trigger a download
      const link = document.createElement('a');
      link.href = downloadTemplateUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    handleOnClickClearFiles();
  }, [downloadTemplateUrl, handleOnClickClearFiles, templateType]);

  const handleOnClickCommit = useCallback(async () => {
    if (!status || !importId) {
      return;
    }
    try {
      setIsCommitting(true);
      await dispatchedCommit(importId, status.transactionId);
    } catch (e) {
      toast.error('Commit failed');
    } finally {
      setIsCommitting(false);
    }
  }, [dispatchedCommit, importId, status]);

  // TODO: fix this later
  /* Grid methods */
  // const gridApiRef = useCallback(
  //   (grid) => {
  //     if (grid && grid.api && notices) {
  //       try {
  //         grid.api.setRowData(
  //           'problems' in notices
  //             ? notices.problems.map((problem) => {
  //               return {
  //                 id: problem.rowId,
  //                 message: problem.message,
  //               };
  //             })
  //             : undefined // Dont put data in if there are no errors
  //         );
  //       } catch (error) {
  //         noop();
  //       }
  //       grid.api.hideOverlay();
  //     }
  //   },
  //   [notices]
  // );
  const gridRef = useRef(null);
  const handleGridReady = useCallback(
    (event: GridReadyEvent) => {
      if (status && status.validationMsg) {
        event.api.setRowData(status.result.messages);
      }
    },
    [status],
  );
  useEffect(() => {
    // When scope changes (or import config changes), reset the wizard to Step 1
    setStatus(null);
    setUploadFileName(null);
    setExpanded(WizardSection.select);
    if (uploadButtonRef.current) {
      uploadButtonRef.current.value = '';
    }
  }, [scopeId]);
  return (
    <div className={bulkImportStyles}>
      <section className="header-content">
        <Subheader title={title} />
      </section>
      <Stepper activeStep={expanded} className="bulk-import-stepper" orientation="vertical">
        <Step expanded={expanded === WizardSection.select}>
          <StepLabel className={'step-header'}>
            <Typography variant={'h6'}>Download a Template</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <React.Fragment>
              <Typography variant="h6" component="h6" className="bulkimport-label">
                {stepper1Label}
              </Typography>
              <Grid container alignItems="center">
                <SquareActionButton
                  type="primary"
                  className="bulkimport-download"
                  disabled={bulkImportLoadingState !== BulkImportLoadingState.idle}
                  icon="fa fa-cloud-download"
                  onClick={handleClickDownloadTemplate}
                  text="Download"
                />
              </Grid>
            </React.Fragment>
            <WizardActions section={WizardSection.select} onContinue={goForward} onGoBack={goBack} disabled={false} />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.upload}>
          <StepLabel className={'step-header'}>
            <Typography variant={'h6'}>Upload a completed Template</Typography>
          </StepLabel>
          <StepContent classes={{ root: 'details' }}>
            <React.Fragment>
              <Grid container alignItems="center">
                <input
                  ref={uploadButtonRef}
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  id="bulkimport-upload-btn"
                  multiple={false}
                  disabled={bulkImportLoadingState !== BulkImportLoadingState.idle || !importId}
                  onChange={handleChangeFileUpload}
                  onClick={handleOnClickClearFiles}
                  style={{ display: 'none' }}
                  type="file"
                />
                <Typography variant="h6" component="h6" className="bulkimport-label">
                  {stepper2Label}
                </Typography>
              </Grid>
              <div>
                <label htmlFor="bulkimport-upload-btn">
                  <SquareActionButton
                    type="primary"
                    className="bulkimport-upload"
                    disabled={bulkImportLoadingState !== BulkImportLoadingState.idle}
                    onClick={() => uploadButtonRef.current?.click()}
                    icon="fa fa-cloud-upload"
                    text="Upload"
                  />
                </label>
                {uploadFileName && (
                  <span className="bulkimport-chosen-file" onClick={() => uploadButtonRef.current?.click()}>
                    <strong>Chosen file: </strong>
                    <span className="bulkimport-chosen-name">{uploadFileName}</span>
                  </span>
                )}
              </div>
            </React.Fragment>
            <WizardActions
              section={WizardSection.upload}
              onContinue={goForward}
              onGoBack={goBack}
              disabled={isNil(status)}
            />
          </StepContent>
        </Step>
        <Step expanded={expanded === WizardSection.notices}>
          <StepLabel className={'step-header'}>
            <Typography variant={'h6'}>Review Upload</Typography>
          </StepLabel>

          <StepContent classes={{ root: 'details' }}>
            <Box position="relative">
              <Box>
                <React.Fragment>
                  <Box component="section" display="flex" className="bulkimport-results">
                    <Typography variant="h6" className="typography">
                      <Box component="section" display="flex">
                        Uploaded: <div>{status?.statistics?.uploaded ?? 0}</div>
                      </Box>
                    </Typography>
                    <Typography variant="h6" className="typography">
                      <Box component="section" display="flex">
                        Valid: <div className="validated">{status?.statistics?.validated ?? 0}</div>
                      </Box>
                    </Typography>
                    <Typography variant="h6" className="typography">
                      <Box component="section" display="flex">
                        Rejected:{' '}
                        <div className={(status?.statistics?.rejected ?? 0) > 0 ? 'rejected' : ''}>
                          {status?.statistics?.rejected ?? 0}
                        </div>
                      </Box>
                    </Typography>
                    {downloadResultUrl && (
                      <Link href={downloadResultUrl} target="_blank" className={'bulkimport-results-download'}>
                        Download Results
                      </Link>
                    )}
                  </Box>
                  <section className="grid-container ag-theme-material" style={{ height: '350px' }}>
                    <AgGridReact
                      ref={gridRef}
                      rowHeight={40}
                      onGridReady={handleGridReady}
                      columnDefs={status?.result.columns.map((col) => ({
                        headerName: col,
                        field: col,
                        resizable: true,
                      }))}
                      defaultColDef={{
                        suppressMovable: true,
                        suppressMenu: true,
                      }}
                    />
                  </section>
                </React.Fragment>

                <div className="bulkimport-commit-btn">
                  <WizardActions
                    section={WizardSection.notices}
                    onContinue={goForward}
                    onGoBack={goBack}
                    disabled={isCommitting} // disable actions during commit
                  />
                  <SquareActionButton
                    type="primary"
                    className="bulkimport-commit"
                    disabled={isCommitting || bulkImportLoadingState !== BulkImportLoadingState.idle}
                    loading={isCommitting}
                    onClick={handleOnClickCommit}
                    icon="fas fa-save"
                    text="Commit"
                  />
                </div>
              </Box>
              {/* Full-section blocking overlay + spinner */}
              <Backdrop
                open={isCommitting}
                style={{
                  zIndex: 2,
                  color: '#fff',
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.7,
                }}
              >
                <CircularProgress />
              </Backdrop>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </div>
  );
};

export default makeUpdateAssortmentFabSensitive(
  connect<BulkImportValueProps, BulkImportDispatchProps, BulkImportOwnProps, AppState>(
    mapStateToProps,
    mapDispatchToProps,
  )(BulkImport),
);
